import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { toastManager } from '@crm/entities/toast';
import { getApiErrorMessage } from '@crm/shared/api';

import { dealApi } from './api-handlers';
import { dealQueryKeys } from './query-keys';

import type { TDeal } from '../model/deal';
import type {
    TPostponedDealsData,
    TUpdatePostponedDealPayload,
} from '../model/postponed-deals';

export const useUpdatePostponedDealMutation = ({ dealId }: { dealId: TDeal['id'] }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: dealQueryKeys.postponedDeals.updateDeal(dealId),
        mutationFn: (payload: TUpdatePostponedDealPayload) =>
            dealApi.updatePostponedDeal({ dealId, payload }),
        onSuccess: async (updatedDeal, payload) => {
            if (payload.postponedUntil !== undefined) {
                await queryClient.invalidateQueries({
                    queryKey: dealQueryKeys.postponedDeals.all(),
                });
                return;
            }

            queryClient.setQueriesData<TPostponedDealsData>(
                { queryKey: dealQueryKeys.postponedDeals.all() },
                (currentData) => {
                    if (!currentData) {
                        return currentData;
                    }

                    const updatedDealIndex = currentData.items.findIndex(
                        (deal) => deal.id === updatedDeal.id
                    );

                    if (updatedDealIndex === -1) {
                        return currentData;
                    }

                    const items = [...currentData.items];
                    items[updatedDealIndex] = {
                        ...items[updatedDealIndex],
                        ...updatedDeal,
                    };

                    return { ...currentData, items };
                }
            );
        },
        onError: (error) => {
            toastManager.onError({
                title: getApiErrorMessage(error, t('crm.common.deal.errors.update')),
            });
        },
    });
};
